/**
 * The one Better Auth client for both apps. Its plugin list mirrors the
 * server's in backend/src/core/auth/auth.ts (dash excepted; it has no client
 * use). After sign-in or sign-out, call resetTrpcClient() so the WebSocket
 * transport re-authenticates.
 */
import {apiKeyClient} from '@better-auth/api-key/client';
import {__APP_ENV__} from '@src/app_env';
import {
   adminClient,
   lastLoginMethodClient,
   organizationClient,
   usernameClient,
} from 'better-auth/client/plugins';
import {createAuthClient} from 'better-auth/vue';

export const authClient = createAuthClient({
   // Origin only — the client defaults to the /api/auth base path.
   baseURL: __APP_ENV__.API_URL,

   // The API is a separate origin, so cookies must be sent explicitly
   fetchOptions: {
      credentials: 'include',
   },

   plugins: [
      usernameClient(),
      adminClient(),
      organizationClient(),
      apiKeyClient(),
      lastLoginMethodClient({domain: getAuthCookieDomain()}),
   ],
});

/** Session payload type (session + user with all plugin fields). */
export type AuthSession = typeof authClient.$Infer.Session;

/**
 * Permissions argument of organization.checkRolePermission; resource keys and
 * actions are type-checked against the configured access-control statements.
 */
export type OrgPermissions = Parameters<
   typeof authClient.organization.checkRolePermission
>[0]['permissions'];

/**
 * Return the cookie domain for the last-login-method cookie: undefined on
 * localhost or a single-label host, '.<root-domain>' otherwise. Must match the server's
 * AUTH_COOKIE_DOMAIN or the browser ignores clearLastUsedLoginMethod().
 */
function getAuthCookieDomain(): string | undefined {
   const hostname = globalThis.location.hostname;
   const labels = hostname.split('.');

   if (hostname === 'localhost' || labels.length < 2) {
      return undefined;
   }

   return `.${labels.slice(-2).join('.')}`;
}
